import type {
  Platform,
  Product,
  Project,
  CreateDraft,
  Script,
  VideoJob,
  VideoStyle,
} from "./types";

export interface CreateProjectInput {
  name: string;
  product: Product;
  script: Script;
  style: VideoStyle;
  avatarId: string;
  voiceId: string;
  platforms: Platform[];
}

export interface GenerateInput {
  platforms: Platform[];
  /** 一次产出几个变体做 A/B */
  variants?: number;
  varyHooks?: boolean;
  varyAvatars?: boolean;
}

/** 服务端返回的账户信息（余额 = 台账求和） */
export interface Me {
  email: string;
  balanceCents: number;
  /** 服务端按 Clerk metadata / ADMIN_EMAILS 判定的后台权限。 */
  isAdmin?: boolean;
}

/** 带状态码和服务端 error 字段的请求错误，402 余额不足等场景用 */
export class ApiError extends Error {
  status: number;
  /** 服务端 JSON body（如 {error, neededCents, balanceCents}），解析失败时为 undefined */
  payload?: Record<string, unknown>;

  constructor(status: number, path: string, payload?: Record<string, unknown>) {
    const msg =
      typeof payload?.error === "string"
        ? payload.error
        : `${status} ${path}`;
    super(msg);
    this.status = status;
    this.payload = payload;
  }
}

async function request<T>(
  baseUrl: string,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: { "content-type": "application/json", ...init?.headers },
  });
  if (!res.ok) {
    const text = await res.text();
    let payload: Record<string, unknown> | undefined;
    try {
      payload = JSON.parse(text) as Record<string, unknown>;
    } catch {
      payload = text ? { error: text.slice(0, 300) } : undefined;
    }
    throw new ApiError(res.status, path, payload);
  }
  return res.json() as Promise<T>;
}

/**
 * Web 传空字符串走同源相对路径；App 传服务端地址。
 */
export function createClient(baseUrl = "") {
  return {
    /** 商品链接 → 结构化商品信息 */
    scrape: (url: string) =>
      request<Product>(baseUrl, "/api/scrape", {
        method: "POST",
        body: JSON.stringify({ url }),
      }),

    /** 商品信息 → 口播脚本 + 分镜 */
    generateScript: (input: {
      product: Product;
      style: VideoStyle;
      platform: Platform;
      language?: string;
    }) =>
      request<Script>(baseUrl, "/api/script", {
        method: "POST",
        body: JSON.stringify(input),
      }),

    listProjects: () => request<Project[]>(baseUrl, "/api/projects"),

    listDrafts: () => request<CreateDraft[]>(baseUrl, "/api/drafts"),
    getDraft: (id: string) => request<CreateDraft>(baseUrl, `/api/drafts/${id}`),
    saveDraft: (input: { id?: string; name: string; step: number; payload: Record<string, unknown> }) =>
      request<CreateDraft>(baseUrl, "/api/drafts", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    deleteDraft: (id: string) =>
      request<{ ok: boolean }>(baseUrl, `/api/drafts/${id}`, { method: "DELETE" }),

    getProject: (id: string) =>
      request<Project>(baseUrl, `/api/projects/${id}`),

    createProject: (input: CreateProjectInput) =>
      request<Project>(baseUrl, "/api/projects", {
        method: "POST",
        body: JSON.stringify(input),
      }),

    /** 下发渲染任务，返回排队中的 job */
    generate: (projectId: string, input: GenerateInput) =>
      request<VideoJob[]>(baseUrl, `/api/projects/${projectId}/generate`, {
        method: "POST",
        body: JSON.stringify(input),
      }),

    getJob: (id: string) => request<VideoJob>(baseUrl, `/api/jobs/${id}`),

    /** 当前账户。后台权限需显式请求，避免余额轮询反复读取 Clerk 用户资料。 */
    me: (authToken?: string, options?: { includeAdmin?: boolean }) =>
      request<Me>(
        baseUrl,
        options?.includeAdmin ? "/api/me?includeAdmin=1" : "/api/me",
        authToken
          ? { headers: { authorization: `Bearer ${authToken}` } }
          : undefined,
      ),

    deleteProject: (id: string) =>
      request<{ ok: boolean }>(baseUrl, `/api/projects/${id}`, {
        method: "DELETE",
      }),

    renameProject: (id: string, name: string) =>
      request<Project>(baseUrl, `/api/projects/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      }),

    /** 重试失败的任务 */
    checkJobImage: (id: string) =>
      request<{ ok: boolean; charged: false; width: number; height: number; contentType: string }>(baseUrl, `/api/jobs/${id}/check-image`, { method: "POST" }),

    retryJob: (id: string) =>
      request<VideoJob>(baseUrl, `/api/jobs/${id}/retry`, { method: "POST" }),
  };
}

export type ApiClient = ReturnType<typeof createClient>;
